import { DateTime } from "luxon";
import { isApiError } from "./services/apiError";
import { AUDIT_LOG_CATEGORIES, AUDIT_LOG_CATEGORY_KINDS, AUDIT_LOG_CATEGORY_BY_KIND } from "./shared/auditCategories";

const smartCompare = (valA, valB) => {
  const isNullish = (v) => v === null || v === undefined;
  if (isNullish(valA) && isNullish(valB)) return 0;
  if (isNullish(valA)) return 1;
  if (isNullish(valB)) return -1;

  if (Array.isArray(valA) && Array.isArray(valB)) {
    const len = Math.min(valA.length, valB.length);
    for (let i = 0; i < len; i++) {
      const cmp = smartCompare(valA[i], valB[i]);
      if (cmp !== 0) return cmp;
    }

    return valA.length - valB.length;
  }

  if (typeof valA === "number" && typeof valB === "number") {
    return valA - valB;
  }

  if (valA instanceof Date && valB instanceof Date) {
    return valA.getTime() - valB.getTime();
  }

  // Fallback string comparison
  return valA.toString().localeCompare(valB.toString(), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

// firstNonBlank picks the first value with a visible character, so a
// whitespace-only field falls through to the next one instead of rendering an
// empty cell.
function firstNonBlank(...values) {
  return values.find((v) => typeof v === "string" && v.trim() !== "") || "";
}

// renderTypedError is the server's own name for a failure, translated, or null
// when the failure does not carry one. The API envelope is {Err, Extra}, both
// of them i18n keys the server chose; an "err."-prefixed message on the error
// itself is the same thing raised client-side.
// Callers with their own fallback use this, since renderError never returns
// empty.
function renderTypedError(t, error) {
  const data = error?.body;
  if (data?.Err) {
    const prefix = t(data.Err);
    if (data.Extra) {
      return `${prefix}: ${data.Extra.startsWith("err.") ? t(data.Extra) : data.Extra}`;
    }
    return prefix;
  }

  const message = error?.message;
  if (typeof message === "string" && message.startsWith("err.")) {
    return t(message);
  }

  return null;
}

const NIL_UUID = "00000000-0000-0000-0000-000000000000";

const knocRoute = (knocId) => ({ name: "AdminKnocsDetail", params: { knocId } });
const integrationRoute = (integrationId) => ({ name: "AdminIntegrationsDetail", params: { integrationId } });
const identityRoute = (identityType) => (identityId) => ({
  name: "AdminIdentityDetail",
  params: { identityType, identityId },
});
const agentRoute = (id) => ({ name: "AdminAgentDetail", params: { id } });
const trustPolicyRoute = (policyId) => ({ name: "AdminTrustPolicyDetail", params: { policyId } });
const agenticRoute = (itemId) => ({ name: "AdminDelegatedItemDetail", params: { kind: "agentic", itemId } });

// TARGET_LINKS maps an entry kind to the id fields that can link its Target
// cell, tried in order, and the admin route each one opens. Only kinds whose
// Target is still there get a link, so no delete appears here. Each id is
// written only by releases that added it, so older rows fall through to text.
const TARGET_LINKS = {
  grantError: [["KnocId", knocRoute]],
  revokeError: [["KnocId", knocRoute]],
  grantForceCleaned: [["KnocId", knocRoute]],
  grantRevokeAbandoned: [["KnocId", knocRoute]],
  grantExpired: [["KnocId", knocRoute]],
  clickedToGrant: [["KnocId", knocRoute]],
  clickedToRevoke: [["KnocId", knocRoute]],
  adminManualGrant: [["KnocId", knocRoute]],
  adminOverrideGrant: [["KnocId", knocRoute]],
  adminRevokeDelegated: [["KnocId", knocRoute]],
  adminRevokeGrant: [["KnocId", knocRoute]],
  trustPolicyBlocked: [["KnocId", knocRoute]],
  trustPolicyChallenged: [["KnocId", knocRoute]],
  trustPolicyObserved: [["KnocId", knocRoute]],
  greynoiseBlocked: [["KnocId", knocRoute]],
  greynoiseLookupFailed: [["KnocId", knocRoute]],
  grantIpExcluded: [["KnocId", knocRoute]],
  discoveredIpFiltered: [["KnocId", knocRoute]],
  createAcl: [["KnocId", knocRoute]],
  updateAcl: [["KnocId", knocRoute]],
  saveKnoc: [["KnocId", knocRoute]],
  // An EDL secret's AclId is the knoc it serves.
  createAllowlistSecret: [["AclId", knocRoute]],
  regenerateAllowlistSecret: [["AclId", knocRoute]],
  // The "knoc" kinds record an integration (a backend), not a knoc.
  createKnoc: [["BackendId", integrationRoute]],
  updateKnoc: [["BackendId", integrationRoute]],
  validateIntegration: [
    ["KnocId", knocRoute],
    ["BackendId", integrationRoute],
  ],
  createGroup: [["GroupId", identityRoute("group")]],
  updateGroup: [["GroupId", identityRoute("group")]],
  createUser: [["UserId", identityRoute("user")]],
  resetUserOtp: [["UserId", identityRoute("user")]],
  updateUser: [["UserId", identityRoute("user")]],
  userChangedPassword: [["UserId", identityRoute("user")]],
  userSessionDeleted: [["UserId", identityRoute("user")]],
  createAdmin: [["TargetAdminId", identityRoute("admin")]],
  resetAdminOtp: [["TargetAdminId", identityRoute("admin")]],
  updateAdmin: [["TargetAdminId", identityRoute("admin")]],
  adminSessionDeleted: [["TargetAdminId", identityRoute("admin")]],
  createAgent: [["AgentId", agentRoute]],
  updateAgent: [["AgentId", agentRoute]],
  agentAdoptedHostname: [["AgentId", agentRoute]],
  createTrustPolicy: [["Id", trustPolicyRoute]],
  updateTrustPolicy: [["Id", trustPolicyRoute]],
  setDefaultTrustPolicy: [["PolicyId", trustPolicyRoute]],
  adminRevokeAgenticAccess: [["AgenticAccessId", agenticRoute]],
};

// Id fields that exist only to drive a link. The detail modal hides them rather
// than showing a raw UUID row.
const AUDIT_LINK_ID_FIELDS = new Set([
  "KnocId",
  "BackendId",
  "GroupId",
  "UserId",
  "AdminId",
  "TargetAdminId",
  "DeletedByAdminId",
  "AgentId",
  "PolicyId",
  "DisplacedPolicyId",
  "AgenticAccessId",
]);

const usableId = (id) => typeof id === "string" && id.trim() !== "" && id !== NIL_UUID;

// On these kinds UserName is the admin account acted on, not a user.
const ADMIN_TARGET_KINDS = new Set(["createAdmin", "resetAdminOtp", "updateAdmin", "deleteAdmin"]);

// FIELD_LINKS maps a named field in an entry to the id field recorded beside it
// and the admin route that id opens. A few depend on the kind, because the same
// field name means a different account there.
const FIELD_LINKS = {
  UserName: (kind) =>
    ADMIN_TARGET_KINDS.has(kind) ? ["TargetAdminId", identityRoute("admin")] : ["UserId", identityRoute("user")],
  // A deleted admin session keeps its owner on AdminName, for older rows.
  AdminName: (kind) =>
    kind === "adminSessionDeleted" ? ["TargetAdminId", identityRoute("admin")] : ["AdminId", identityRoute("admin")],
  DeletedByAdminName: () => ["DeletedByAdminId", identityRoute("admin")],
  AgentName: () => ["AgentId", agentRoute],
  KnocName: () => ["KnocId", knocRoute],
  KnocDescription: () => ["KnocId", knocRoute],
  AclDescription: () => ["KnocId", knocRoute],
  // An EDL secret names no knoc, so its AclId stays on screen and links itself.
  AclId: () => ["AclId", knocRoute],
  PolicyName: () => ["PolicyId", trustPolicyRoute],
  DisplacedName: () => ["DisplacedPolicyId", trustPolicyRoute],
};

function logEntryTargetLink(entry) {
  if (!entry) return null;
  const kind = Object.keys(entry)[0];
  const links = TARGET_LINKS[kind];
  if (!links) return null;
  const data = entry[kind] || {};
  for (const [field, route] of links) {
    // An older server re-marshals an absent id as the nil UUID.
    if (usableId(data[field])) return route(data[field]);
  }
  return null;
}

function logEntryFieldLink(entry, field) {
  if (!entry || !field) return null;
  const kind = Object.keys(entry)[0];
  const data = entry[kind] || {};
  const link = FIELD_LINKS[field];
  if (link) {
    const [idField, route] = link(kind);
    return usableId(data[idField]) ? route(data[idField]) : null;
  }
  // Anything else links only when it is what the Target column shows.
  const value = data[field];
  if (typeof value !== "string" || value.trim() === "") return null;
  return value === logEntryTarget(entry) ? logEntryTargetLink(entry) : null;
}

// logEntryTarget returns a short human-readable label for what the action
// affected, the ACL/group/knoc/user/agent/api-key on the receiving end of
// the event, distinct from the actor in logEntryActor. Returns "" when the
// entry has no meaningful target (e.g. session expiries, simple login/out).
function logEntryTarget(entry) {
  if (!entry) return "";
  const kind = Object.keys(entry)[0];
  const data = entry[kind] || {};
  switch (kind) {
    case "createAcl":
    case "updateAcl":
    case "deleteAcl":
      return data.Description || "";
    case "createGroup":
    case "updateGroup":
    case "deleteGroup":
      return data.Name || data.Description || "";
    case "createKnoc":
    case "updateKnoc":
    case "deleteKnoc":
      return data.Name || "";
    // A validation run against an unsaved draft has no name, so it falls
    // back to the backend type, which is all that identifies the draft.
    case "validateIntegration":
      return data.Name || data.Type || "";
    // The device the key was asked of, which is all a key exchange is about.
    case "generateApiKey":
      return data.Name || data.Type || "";
    case "saveKnoc":
      return data.KnocName || "";
    case "createUser":
    case "resetUserOtp":
    case "updateUser":
    case "userChangedPassword":
    case "deleteUser":
    case "createAdmin":
    case "resetAdminOtp":
    case "updateAdmin":
    case "deleteAdmin":
      return data.UserName || data.Name || "";
    case "createAgent":
    case "updateAgent":
    case "deleteAgent":
    case "agentAdoptedHostname":
      return data.Name || "";
    case "createApiKey":
    case "updateApiKey":
    case "deleteApiKey":
    case "createAllowlistSecret":
    case "regenerateAllowlistSecret":
    case "hashAllowlistSecret":
    case "updateAllowlistSecret":
      return data.Id ? String(data.Id) : "";
    case "clickedToGrant":
    case "adminManualGrant":
    case "adminOverrideGrant":
    case "greynoiseBlocked":
    case "greynoiseLookupFailed":
    case "threatIntelStaticBlocked":
    case "grantIpExcluded":
    case "discoveredIpFiltered":
      return data.KnocDescription || "";
    case "trustPolicyBlocked":
    case "trustPolicyChallenged":
    case "trustPolicyObserved":
      return data.KnocDescription || data.AclDescription || "";
    case "createTrustPolicy":
    case "updateTrustPolicy":
    case "deleteTrustPolicy":
    case "setDefaultTrustPolicy":
      return data.Name || data.PolicyName || "";
    case "clickedToRevoke":
    case "grantExpired":
    case "adminRevokeDelegated":
    case "adminRevokeGrant":
      return data.AclDescription || "";
    case "adminRevokeAgenticAccess":
      return data.Name || data.UserName || "";
    // AgentName is the fallback for rows written before these entries carried the knoc.
    // A whitespace-only description is truthy, so it would win and render blank.
    case "grantError":
    case "revokeError":
    case "grantForceCleaned":
    case "grantRevokeAbandoned":
      return firstNonBlank(data.KnocDescription, data.AclDescription, data.AgentName);
    // Session owner is the target only when the entry has actor
    // attribution; legacy rows show the owner in the actor column instead.
    case "userSessionDeleted":
      return data.AdminName ? data.UserName || "" : "";
    case "adminSessionDeleted":
      return data.DeletedByAdminName ? data.AdminName || "" : "";
    default:
      return "";
  }
}

// logEntryActorField names the field logEntryActor reads, so the Username
// column can link the same account it shows.
function logEntryActorField(entry) {
  const kind = Object.keys(entry || {})[0];
  const data = entry?.[kind] || {};
  // Session deletions record the acting admin on a dedicated field; rows
  // written before actor attribution lack it and fall through to the
  // generic owner-as-actor behaviour below.
  if (kind === "userSessionDeleted" && data.AdminName) return "AdminName";
  if (kind === "adminSessionDeleted" && data.DeletedByAdminName) return "DeletedByAdminName";
  if (data.UserName) return "UserName";
  if (data.AdminName) return "AdminName";
  return null;
}

export default {
  statuses: {
    0: { icon: "text-success bg-success/10", name: "msg.granted" },
    1: { icon: "text-danger bg-destructive/10", name: "msg.error" },
    2: { icon: "text-info bg-info/10", name: "msg.pending" },
    3: { icon: "text-danger bg-destructive/10", name: "msg.agentOffline" },
    4: { icon: "text-muted-foreground bg-muted-foreground/10", name: "msg.waiting" },
    5: { icon: "text-success bg-success/10", name: "msg.read" },
    6: { icon: "text-info bg-info/10", name: "msg.published" },
    8: { icon: "text-info bg-info/10", name: "msg.pending" },
    9: { icon: "text-muted-foreground bg-muted-foreground/10", name: "msg.revoked" },
    10: { icon: "text-danger bg-destructive/10", name: "msg.error" },
  },
  timeRemaining: (time) => {
    const date = DateTime.fromISO(time);
    const now = DateTime.now();

    return date.diff(now).milliseconds;
  },
  formatDuration: (ms, _t) => {
    if (ms === null || ms === undefined || ms < 0) {
      return "00:00:00";
    }

    const pad = (n) => Math.floor(n).toString().padStart(2, "0");

    const totalSeconds = Math.floor(ms / 1000);
    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const totalHours = Math.floor(totalMinutes / 60);
    const hours = totalHours % 24;
    const days = Math.floor(totalHours / 24);

    if (days > 0) {
      return `${days}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  },
  formatTime: (time) => {
    if (time === null || time === undefined) {
      return `00:00:00`;
    } else {
      const d = new Date(time);

      const h = d.getHours().toString().padStart(2, "0");
      const m = d.getMinutes().toString().padStart(2, "0");
      const s = d.getSeconds().toString().padStart(2, "0");

      return `${h}:${m}:${s}`;
    }
  },
  // Coarse human duration for the "more than X days/hours of access" line.
  // Returns days when ms >= 24h, otherwise hours. It floors, and the caller is
  // expected to phrase as "more than {x}" so the rounding-down is safe.
  formatCoarseDuration: (ms, t) => {
    if (ms === null || ms === undefined || ms < 0) return "";
    const HOUR = 60 * 60 * 1000;
    const DAY = 24 * HOUR;
    if (ms >= DAY) {
      const days = Math.floor(ms / DAY);
      return t ? t("msg.durationDays", { count: days }, days) : `${days} ${days === 1 ? "day" : "days"}`;
    }
    const hours = Math.floor(ms / HOUR);
    return t ? t("msg.durationHours", { count: hours }, hours) : `${hours} ${hours === 1 ? "hour" : "hours"}`;
  },
  formatAsDays: (time) => {
    const date = DateTime.fromISO(time);
    const now = DateTime.now();
    const days = Math.floor(date.diff(now).milliseconds / (1000 * 60 * 60 * 24));

    if (days === 1) {
      return "1 day";
    } else if (days === 0) {
      return "today";
    } else if (days == -1) {
      return "1 day ago";
    } else if (days < -1) {
      return `${-days} days ago`;
    } else {
      return `${days} days`;
    }
  },
  marshalDateTime: (date) => {
    if (typeof date === "undefined" || date === "" || date === null) {
      return null;
    }

    return new Date(date).toISOString();
  },
  formatDateTime(date) {
    if (!date) return "";

    const d = new Date(date);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");

    const h = d.getHours().toString().padStart(2, "0");
    const m = d.getMinutes().toString().padStart(2, "0");

    return `${year}-${month}-${day}T${h}:${m}`;
  },
  logEntryActor: (entry) => {
    const kind = Object.keys(entry)[0];
    const field = logEntryActorField(entry);
    return field ? entry[kind][field] : "";
  },
  // logEntryActorLink returns the admin route for the account the Username
  // column shows, or null on a row written before actor ids were recorded.
  logEntryActorLink: (entry) => logEntryFieldLink(entry, logEntryActorField(entry)),
  // logEntryActorIdp returns the acting user's login IdP, for the badge telling two
  // accounts of one username apart. Empty for an admin, a local account, or an old row.
  logEntryActorIdp: (entry) => {
    const kind = Object.keys(entry || {})[0];
    const data = entry?.[kind] || {};
    // A session an admin deleted is the admin's action, so the user's provider
    // would label the wrong person.
    if (kind === "userSessionDeleted" && data.AdminName) return "";
    return data.UserName ? data.UserIdpKey || "" : "";
  },
  logEntryAction: (entry) => {
    return "logEntryKind." + Object.keys(entry)[0];
  },
  logEntryIp: (entry) => {
    const first = Object.values(entry || {})[0];
    return first?.IpAddress || first?.RequestingIpAddr || null;
  },
  logEntryTarget,
  // logEntryTargetLink returns the admin route for what a row's Target cell
  // names, or null when the kind has no page or the row predates its id. A
  // grant has no page of its own, so a grant row links to its knoc. Never
  // links to the agent on a grant row.
  logEntryTargetLink,
  // logEntryFieldLink returns the admin route for one field of an entry, for
  // the detail modal, which links the value itself, or null when nothing it
  // names has a page.
  logEntryFieldLink,
  // logEntryCategory groups entry kinds for the coloured dot on a log row (the
  // audit log and a user's recent activity).
  logEntryCategory: (kind) => {
    return AUDIT_LOG_CATEGORY_BY_KIND[kind] || "other";
  },
  AUDIT_LOG_CATEGORIES,
  AUDIT_LOG_CATEGORY_KINDS,
  AUDIT_LINK_ID_FIELDS,
  /**
   * Unwrap error and turn it into something user-friendly
   *
   * @param {object} error
   */
  renderError: (t, error) => {
    // The response policy has already acted on it.
    if (error?.handled) {
      return "";
    }

    const typed = renderTypedError(t, error);
    if (typed) {
      return typed;
    }

    if (isApiError(error)) {
      if (error.kind === "network") return t("err.requestNetwork");
      if (error.kind === "timeout") return t("err.requestTimedOut");
      if (error.kind === "aborted") return t("err.requestCancelled");
      return error.status >= 500 ? t("err.internalServerError") : t("err.requestRefused");
    }

    if (error?.message) {
      return error.message;
    }

    return t("err.internalServerError");
  },
  /**
   * The server's own name for a failure, or null when it carries none. Use this
   * rather than renderError wherever the caller has its own message to fall
   * back to.
   *
   * @param {Function} t
   * @param {object} error
   * @returns {string|null}
   */
  renderTypedError,
  btnClass: (theme, hc) => {
    return {
      "bg-theme-teal": theme == "teal" && !hc,
      "bg-theme-teal-hc": theme == "teal" && hc,
      "bg-theme-grape": theme == "grape" && !hc,
      "bg-theme-grape-hc": theme == "grape" && hc,
      "bg-theme-iceberg": theme == "iceberg" && !hc,
      "bg-theme-iceberg-hc": theme == "iceberg" && hc,
      "bg-theme-rose": theme == "rose" && !hc,
      "bg-theme-rose-hc": theme == "rose" && hc,
      "bg-theme-offsite": theme == "offsite" && !hc,
      "bg-theme-offsite-hc": theme == "offsite" && hc,
    };
  },
  sortObjBy: (val) => (obj) => obj[val],
  id: (x) => x,
  smartCompare,
  smartSortFunc: (key) => {
    return (a, b) => smartCompare(key(a), key(b));
  },
  icons: {},
};
